import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface SubLevel {
  inputType: string;
  minValue: number;
  maxValue: number;
}

interface LevelData {
  shuffle: boolean;
  subLevels: SubLevel[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelData[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const FRUIT = '🍊';
const MAX_FRUIT = 10;

export function mountLearnQuantities(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const subLevels = buildSubLevels(level);
  const targets = subLevels.map((s) => randInt(s.minValue, s.maxValue));

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Quantidades</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage learn-quantities">
      <div class="feedback" aria-live="polite"></div>
      <p class="learn-quantities__progress" aria-live="polite"></p>
      <p class="learn-quantities__prompt"></p>
      <div class="learn-quantities__basket" aria-live="polite" aria-label="Sua cesta"></div>
      <div class="learn-quantities__controls">
        <button class="lq-btn lq-btn--minus" type="button" aria-label="Tirar uma laranja">−</button>
        <div class="learn-quantities__count" aria-live="polite">0</div>
        <button class="lq-btn lq-btn--plus" type="button" aria-label="Colocar uma laranja">+</button>
      </div>
      <button class="btn btn--primary learn-quantities__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.learn-quantities__progress')!;
  const promptEl = page.querySelector<HTMLElement>('.learn-quantities__prompt')!;
  const basketEl = page.querySelector<HTMLElement>('.learn-quantities__basket')!;
  const countEl = page.querySelector<HTMLElement>('.learn-quantities__count')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  let index = 0;
  let current = 0;
  let correctCount = 0;

  function renderBasket(): void {
    basketEl.textContent = FRUIT.repeat(current);
    countEl.textContent = String(current);
  }

  function renderQuestion(): void {
    if (index >= targets.length) {
      finishLevel();
      return;
    }
    const target = targets[index]!;
    progressEl.textContent = `Questão ${index + 1} de ${targets.length}`;
    promptEl.innerHTML = `Mostre <strong>${target}</strong> ${target === 1 ? 'laranja' : 'laranjas'}.`;
    current = 0;
    renderBasket();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function add(): void {
    if (current >= MAX_FRUIT) return;
    current++;
    ctx.audio.play('click');
    renderBasket();
  }

  function sub(): void {
    if (current <= 0) return;
    current--;
    ctx.audio.play('click');
    renderBasket();
  }

  function confirm(): void {
    const target = targets[index]!;
    if (current === target) {
      ctx.audio.play('correct');
      correctCount++;
      showFeedback('ok');
      index++;
      window.setTimeout(renderQuestion, 700);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  page.querySelector<HTMLElement>('.lq-btn--plus')!.addEventListener('click', add);
  page.querySelector<HTMLElement>('.lq-btn--minus')!.addEventListener('click', sub);
  page.querySelector<HTMLElement>('.learn-quantities__ok')!.addEventListener('click', confirm);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === '+' || e.key === 'ArrowUp' || e.key === 'ArrowRight') add();
    else if (e.key === '-' || e.key === 'ArrowDown' || e.key === 'ArrowLeft') sub();
    else if (e.key === 'Enter') confirm();
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${targets.length}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/learn_quantities/${level.level + 1}`;
      });
    }
  }

  renderQuestion();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function buildSubLevels(level: Level): SubLevel[] {
  const cfg = level.data[0];
  if (!cfg) return [];
  const list = [...cfg.subLevels];
  return cfg.shuffle ? shuffle(list) : list;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
